/**
 * La pestaña Corrección del panel de Word: los datos que da el motor y las frases que se pintan.
 * Sin React y sin red, para poder probarlo solo.
 */

export type Cuenta = { cambios: number; aceptados: number; rechazados: number; retocados: number; tuyos: number; no_evaluables: number; palabras_no_tuyas: number; corregidas: number };
export type Duda = { capitulo: string; asuntos: string[] };
export type PasadaHecha = {
  k: number; pasada: string; cuando: string; fichero: string | null; sigue: boolean;
  cambios: number; palabras_no_tuyas: number; corregidas: number; coste: number; para_el_autor: Duda[]; sin_tocar: number; sin_contar?: number; leida: Cuenta | null;
};
export type Pasada = { id: string; nombre: string; hace: string; no_hace: string; hechas: PasadaHecha[] };
export type Resultado = { sin_cambios?: boolean; fichero: string | null; cambios: number; palabras_no_tuyas?: number; corregidas?: number; sin_ancla?: number; sin_tocar?: number; sin_contar?: number; prosa_quitada?: number; para_el_autor?: Duda[]; coste?: number; abierto?: boolean };
export type Trabajo = {
  id: string; estado: "en_curso" | "hecho" | "fallido" | "interrumpido"; titulo: string;
  progreso: { hecho: number; total: number } | null; pasos: { titulo: string; estado: string }[] | null;
  error: string | null; resultado: Resultado | null; inicio: string; fin: string | null;
};
export type Correccion = {
  hay: true; titulo: string | null; hay_motor: boolean;
  novela: { fichero: string; cuando: string; escenas: number };
  actual: { fichero: string; existe: boolean; marcas: number | null; es_la_construida: boolean; partes: number | null; palabras: number | null };
  pasadas: Pasada[]; siguiente: string | null; gasto: number; trabajo: Trabajo | null;
  /** Versiones que están en la carpeta y no en la lista: restos de una pasada que se cortó. No cuentan. */
  sueltas?: string[];
  /** Con una versión de la novela abierta en Word: cuál es, y si es la última. */
  abierta?: { fichero: string; es_la_ultima: boolean } | null;
};
export type SinCorreccion = { hay: false; motivo: "sin_guion" | "sin_novela" | "varios_libros"; titulo?: string | null; hay_motor?: boolean; trabajo: Trabajo | null };

export const miles = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
export const dinero = (x: number) => { const c = Math.round(100 * (Number(x) || 0)); return `${miles(Math.floor(c / 100))},${String(c % 100).padStart(2, "0")} $`; };
const veces = (n: number, uno: string, varios: string) => `${miles(n)} ${n === 1 ? uno : varios}`;
export function diaYHora(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export const enMarcha = (t: Trabajo | null | undefined) => t?.estado === "en_curso";

/**
 * Por qué ahora mismo no se puede pasar ninguna pasada, o `null` si se puede. Lo dice el motor al
 * intentarlo; aquí se dice ANTES, para no ofrecer un botón que va a contestar que no.
 */
export function porQueNo(c: Correccion): string | null {
  if (!c.hay_motor) return "Esta instalación de HumanInk todavía no sabe corregir la novela. Actualízala.";
  if (enMarcha(c.trabajo)) return "Hay una pasada en marcha.";
  if (!c.actual.existe) return `No encuentro «${c.actual.fichero}» en la carpeta HumanInk del libro. Vuelve a construir la novela en la pestaña Guion.`;
  if (c.actual.marcas === null) return `No he podido leer «${c.actual.fichero}». Guárdalo en Word y pulsa Actualizar.`;
  if (c.actual.marcas > 0) return `«${c.actual.fichero}» tiene ${veces(c.actual.marcas, "cambio", "cambios")} sin aceptar ni rechazar. Decide en Word (Revisar → Aceptar o Rechazar), guarda y vuelve: una pasada se escribe sobre un texto decidido.`;
  return null;
}

/** Lo que va a viajar si pasa una pasada ahora, dicho antes de gastar. */
export function fraseDeLoQueViaja(c: Correccion): string {
  const { palabras, partes } = c.actual;
  const tamano = palabras != null && partes != null ? `${veces(palabras, "palabra", "palabras")} en ${veces(partes, "parte", "partes")}` : "La novela entera";
  const ultima = c.pasadas.flatMap((p) => p.hechas).sort((a, b) => b.k - a.k)[0];
  return `${tamano}: una llamada al modelo por parte, de tu llave de OpenRouter.${ultima ? ` La última pasada costó ${dinero(ultima.coste)}.` : ""}`;
}

/** Qué hizo el autor con los cambios de una pasada, en una frase. */
export function fraseDeLoDecidido(l: Cuenta): string {
  const partes = [
    l.aceptados ? `aceptaste ${miles(l.aceptados)}` : "",
    l.rechazados ? `rechazaste ${miles(l.rechazados)}` : "",
    l.retocados ? `retocaste ${miles(l.retocados)}` : "",
    l.tuyos ? `en ${miles(l.tuyos)} escribiste otra cosa` : "",
    l.no_evaluables ? veces(l.no_evaluables, "no se puede saber", "no se pueden saber") : "",
  ].filter(Boolean);
  const queda = l.palabras_no_tuyas ? ` ${l.palabras_no_tuyas === 1 ? "Se queda" : "Se quedan"} ${veces(l.palabras_no_tuyas, "palabra que no era tuya", "palabras que no eran tuyas")}.` : l.aceptados || l.retocados ? " No se queda ninguna palabra que no fuera tuya." : "";
  return `${partes.length ? partes.join(", ") : "sin cambios que leer"}.${queda}`;
}

/**
 * Una pasada ya hecha, en una línea. Dice siempre si dejó un Word y cuál (Rais, 14-oct, tras probarlo: «en los demás
 * pone que está hecha la corrección pero no sé dónde está»): una pasada que no cambia nada NO crea documento.
 */
export function fraseDeHecha(h: PasadaHecha): string {
  const cuando = diaYHora(h.cuando);
  if (!h.fichero) return `${cuando} · no encontró nada que cambiar, así que no creó ningún Word · ${dinero(h.coste)}`;
  const propuso = `${veces(h.cambios, "cambio propuesto", "cambios propuestos")}${h.sin_contar ? ` (${veces(h.sin_contar, "párrafo", "párrafos")} sin contar)` : ""}`;
  if (!h.sigue) return `${cuando} · ${propuso} · ya no está «${h.fichero}» · ${dinero(h.coste)}`;
  return `${cuando} · ${propuso} en «${h.fichero}» · ${h.leida ? fraseDeLoDecidido(h.leida) : "por decidir en Word."} · ${dinero(h.coste)}`;
}

/**
 * ¿Se puede abrir en Word la versión que dejó esta pasada? Solo si dejó un Word y sigue en la carpeta (encargo 0002,
 * 24-oct-2026: antes solo se abría la última, y para volver a mirar una anterior había que buscarla a mano).
 */
export const puedeAbrirse = (h: PasadaHecha) => !!h.fichero && h.sigue;

/** Lo que se dice si Word no ha abierto una versión: dónde está, para abrirla a mano. */
export const fraseDeNoAbierta = (fichero: string) => `No he podido abrir «${fichero}». Está en la carpeta HumanInk del libro.`;
/** La del botón de una pasada: va debajo de su línea, que ya dice qué versión es. */
export const NO_ABIERTA_LA_DE_LA_LINEA = "No he podido abrir esta versión. Ábrela tú desde la carpeta HumanInk del libro.";

/** Por dónde va la pasada en marcha. */
export function fraseDeAvance(t: Trabajo): string {
  const total = t.pasos?.length ?? t.progreso?.total ?? 0;
  const hecho = Math.min(t.progreso?.hecho ?? 0, Math.max(0, total - 1));
  const paso = t.pasos?.[hecho]?.titulo ?? "";
  return total ? `Paso ${hecho + 1} de ${total}${paso ? ` · ${paso}` : ""}` : "Empezando…";
}
export const avanceDe = (t: Trabajo) => { const total = t.pasos?.length ?? t.progreso?.total ?? 0; return total ? Math.min(1, (t.progreso?.hecho ?? 0) / total) : 0; };

/** Lo que dejó la última pasada, dicho al terminar. */
export function fraseDeResultado(r: Resultado): string {
  if (r.sin_cambios || !r.fichero) return "El corrector no ha encontrado nada que cambiar en esta pasada, así que no ha creado ningún Word nuevo: la novela sigue en la versión que estaba.";
  const cortadas = r.sin_tocar ? ` ${veces(r.sin_tocar, "parte volvió cortada y se ha quedado", "partes volvieron cortadas y se han quedado")} como estaba.` : "";
  const sinSitio = r.sin_ancla ? ` ${veces(r.sin_ancla, "cambio no encontró su sitio y no se ha escrito", "cambios no encontraron su sitio y no se han escrito")}.` : "";
  const quitada = r.prosa_quitada ? ` El corrector añadía ${veces(r.prosa_quitada, "párrafo suyo", "párrafos suyos")} y no ${r.prosa_quitada === 1 ? "ha" : "han"} entrado: tu perfil dice que no quieres prosa de la IA.` : "";
  const sinContar = r.sin_contar ? ` ${veces(r.sin_contar, "párrafo cambiado no se ha podido apuntar con detalle: sus palabras no entran", "párrafos cambiados no se han podido apuntar con detalle: sus palabras no entran")} en la cuenta de lo que no era tuyo.` : "";
  return `${veces(r.cambios, "cambio marcado", "cambios marcados")} en «${r.fichero}»${r.abierto ? ", que se ha abierto en Word" : ""}. Decide allí cada uno (Revisar → Aceptar o Rechazar), guarda y vuelve.${cortadas}${sinSitio}${quitada}${sinContar}`;
}

/**
 * Con una versión de la novela abierta que no es la última: se dice, porque decidir cambios en una versión
 * vieja no sirve de nada (las pasadas parten siempre de la última).
 */
export function fraseDeAbierta(c: Correccion): string | null {
  const a = c.abierta;
  if (!a || a.es_la_ultima) return null;
  return `Tienes abierta «${a.fichero}», pero la última versión de la novela es «${c.actual.fichero}». Las pasadas parten de la última: ábrela con el botón de arriba y trabaja en esa.`;
}

/** Lo que se dice de los ficheros de una pasada que se cortó a medias, o `null` si no hay ninguno. */
export function fraseDeSueltas(c: Correccion): string | null {
  const s = c.sueltas ?? [];
  if (!s.length) return null;
  return s.length === 1
    ? `En la carpeta hay un fichero de una pasada que no llegó a terminar: «${s[0]}». No cuenta como versión y no se va a usar; puedes borrarlo.`
    : `En la carpeta hay ${miles(s.length)} ficheros de pasadas que no llegaron a terminar (${s.map((f) => `«${f}»`).join(", ")}). No cuentan como versión y no se van a usar; puedes borrarlos.`;
}

/**
 * Qué se dice de la última pasada lanzada, o `null` si ya no viene a cuento. El motor recuerda el último
 * trabajo para siempre; la pantalla solo lo enseña mientras sigue siendo noticia:
 *   · en marcha, siempre;
 *   · hecha, mientras su versión espera a que el autor decida (o si es la que esta página ha visto trabajar);
 *   · fallida, mientras no se haya hecho otra pasada después.
 */
export function noticiaDe(c: Correccion, seguida: string | null): "en_marcha" | "hecha" | "fallida" | null {
  const t = c.trabajo;
  if (!t) return null;
  if (t.estado === "en_curso") return "en_marcha";
  if (t.estado === "hecho") {
    if (!t.resultado) return null;
    const espera = !!t.resultado.fichero && t.resultado.fichero === c.actual.fichero && (c.actual.marcas ?? 0) > 0;
    return espera || t.id === seguida ? "hecha" : null;
  }
  const ultima = c.pasadas.flatMap((p) => p.hechas).map((h) => h.cuando).sort().pop() ?? "";
  return t.id === seguida || String(t.inicio) > ultima ? "fallida" : null;
}

/** Las dudas que el corrector deja al autor en la versión que hay ahora, si las hay. */
export function dudasDeAhora(c: Correccion): Duda[] {
  const suya = c.pasadas.flatMap((p) => p.hechas).find((h) => h.fichero === c.actual.fichero);
  return (suya?.para_el_autor ?? []).filter((d) => d.asuntos?.length);
}

/** ¿Está decidida la última pasada y todavía sin leer? Entonces se ofrece leerla. */
export function quedaPorLeer(c: Correccion): boolean {
  const suya = c.pasadas.flatMap((p) => p.hechas).find((h) => h.fichero === c.actual.fichero);
  return !!suya && !suya.leida && c.actual.marcas === 0 && !enMarcha(c.trabajo);
}
